import { render } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { api, ApiError, type Me } from './api.js';
import { GuildPage } from './guild.js';
import { Card, GuildIcon, Link, Notice, PlanBadge, Spinner, Toggle, usePath } from './ui.js';

type MeState = { kind: 'loading' } | { kind: 'anon'; loginEnabled: boolean; error?: string } | { kind: 'ok'; me: Me } | { kind: 'error'; error: string };

function loginHref(): string {
  return `/login?next=${encodeURIComponent(location.pathname + location.search)}`;
}

function App() {
  const path = usePath();
  const [state, setState] = useState<MeState>({ kind: 'loading' });

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ok', me: await api.me() });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setState({ kind: 'anon', loginEnabled: err.body.loginEnabled !== false, error: err.message });
      } else {
        setState({ kind: 'error', error: (err as Error).message });
      }
    }
  }, []);
  useEffect(() => void load(), [load]);

  const logout = async () => {
    await api.logout().catch(() => {});
    location.href = '/';
  };

  const me = state.kind === 'ok' ? state.me : null;
  const parts = path.split('/').filter(Boolean); // ['dashboard', guildId?, tab?]
  const guildId = parts[1] && parts[1] !== 'me' ? parts[1] : null;

  return (
    <>
      <header class="topbar">
        <div class="topbar-inner">
          <Link href="/dashboard" class="brand">
            <span class="brand-dot" aria-hidden="true" />
            Listener Bot
          </Link>
          {me && (
            <nav class="account">
              <Link href="/dashboard/me" class="account-link">
                {me.user.avatarUrl && <img src={me.user.avatarUrl} alt="" width={24} height={24} referrerPolicy="no-referrer" />}
                <span>{me.user.username}</span>
              </Link>
              <button type="button" class="btn ghost small" onClick={logout}>
                Log out
              </button>
            </nav>
          )}
        </div>
      </header>
      <main class="page dash">
        {state.kind === 'loading' && <Spinner />}
        {state.kind === 'error' && <Notice kind="error">{state.error}</Notice>}
        {state.kind === 'anon' && <Login enabled={state.loginEnabled} />}
        {me && (guildId ? <GuildPage key={guildId} guildId={guildId} tab={parts[2] ?? 'overview'} me={me} /> : parts[1] === 'me' ? <Privacy me={me} onChange={load} /> : <Picker me={me} />)}
      </main>
    </>
  );
}

function Login(props: { enabled: boolean }) {
  return (
    <div class="hero">
      <h1>Bot dashboard</h1>
      <p class="muted">Choose where the listener goes, what it records and for how long, and manage Premium for your servers.</p>
      {props.enabled ? (
        <a class="btn primary big" href={loginHref()}>
          Log in with Discord
        </a>
      ) : (
        <Notice kind="warn">Logging in isn't set up on this bot yet. The operator needs to add DISCORD_CLIENT_SECRET.</Notice>
      )}
      <p class="muted small">We only ask Discord for your name, avatar and the list of your servers.</p>
    </div>
  );
}

function Picker(props: { me: Me }) {
  const { guilds } = props.me;
  return (
    <>
      <div class="page-head">
        <div>
          <h1>Your servers</h1>
          <p class="muted">Servers where you have the Manage Server permission.</p>
        </div>
        {props.me.inviteUrl && (
          <a class="btn" href={props.me.inviteUrl} target="_blank" rel="noopener">
            Add to another server
          </a>
        )}
      </div>
      {guilds.length === 0 ? (
        <Card>
          <p>You don't manage any servers yet. Ask a server owner to give you Manage Server, or add the bot to your own server.</p>
        </Card>
      ) : (
        <ul class="guild-grid">
          {guilds.map((g) => (
            <li key={g.id}>
              {g.botPresent ? (
                <Link href={`/dashboard/${g.id}`} class="guild-card">
                  <GuildIcon name={g.name} iconUrl={g.iconUrl} size={48} />
                  <span class="guild-name">{g.name}</span>
                  <PlanBadge plan={g.plan} />
                </Link>
              ) : (
                <a href={g.inviteUrl ?? '#'} target="_blank" rel="noopener" class="guild-card absent">
                  <GuildIcon name={g.name} iconUrl={g.iconUrl} size={48} />
                  <span class="guild-name">{g.name}</span>
                  <span class="badge">Add bot</span>
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function Privacy(props: { me: Me; onChange: () => void }) {
  const [optedOut, setOptedOut] = useState(props.me.optedOut);
  const [error, setError] = useState<string | null>(null);
  const change = async (on: boolean) => {
    setOptedOut(on);
    setError(null);
    try {
      setOptedOut((await api.setOptOut(on)).optedOut);
      props.onChange();
    } catch (err) {
      setOptedOut(!on);
      setError((err as Error).message);
    }
  };
  return (
    <>
      <div class="page-head">
        <div>
          <h1>Your privacy</h1>
          <p class="muted">Applies in every server the bot is in, the same as /optout and /optin.</p>
        </div>
      </div>
      <Card>
        <div class="setting">
          <div class="setting-text">
            <div class="setting-label">Never capture my voice</div>
            <div class="setting-hint">
              The bot ignores your audio (and camera) everywhere: nothing in its replay buffer, clips or recordings. Turning this on also drops what it's
              holding from you right now. Recordings that already finished are not changed.
            </div>
          </div>
          <div class="setting-control">
            <Toggle checked={optedOut} label="Never capture my voice" onChange={change} />
          </div>
        </div>
        {error && <Notice kind="error">{error}</Notice>}
      </Card>
      <p class="muted small">
        To have a finished recording removed, ask that server's moderators (they can delete it in the dashboard) or the bot operator. See the{' '}
        <a href="/privacy">privacy policy</a>.
      </p>
    </>
  );
}

render(<App />, document.getElementById('app')!);
